import { useTranslation } from 'react-i18next';
import type { CategoryOption } from '@/types';
import { categoryOptionLabel } from '@/lib/category';
import { useState, useEffect, type ReactNode } from 'react';
import { fetchCategoryOptions, getCalendarUrlWithCategory, getCalendarSubscribeUrlWithCategory } from '@/services/api';

function categoryChipClassName(selected: boolean): string {
  const base = 'text-xs uppercase tracking-[0.14em] transition-colors';
  if (selected) {
    return `${base} text-ink underline decoration-accent decoration-2 underline-offset-[0.45em]`;
  }
  return `${base} text-ink-2 hover:text-ink`;
}

/* Section 03: the subscription block. Category filter as small-caps text toggles,
   the ICS address on an underlined monospace field, actions as text links. */
export function SubscribeCard() {
  const { t } = useTranslation('public');
  const [copied, setCopied] = useState(false);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const loadCategories = async () => {
      try {
        const data = await fetchCategoryOptions(controller.signal);
        if (!controller.signal.aborted) setCategories(data);
      } catch (err) {
        if (!controller.signal.aborted) console.error('Failed to fetch categories:', err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    void loadCategories();
    return () => controller.abort();
  }, []);

  // 未选择分类时返回完整日历地址
  const currentUrl = getCalendarUrlWithCategory(selectedCategory);
  const selectedOption = categories.find(category => category.slug === selectedCategory);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSubscribe = () => {
    window.location.href = getCalendarSubscribeUrlWithCategory(selectedCategory);
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(prev => prev === category ? null : category);
  };

  const clearCategory = () => {
    setSelectedCategory(null);
  };

  let categoryList: ReactNode = (
    <p className="m-0 mt-3 text-sm text-ink-2">{t('subscribe.empty')}</p>
  );
  if (loading) {
    categoryList = <p className="m-0 mt-3 text-sm text-ink-2">{t('subscribe.loading')}</p>;
  } else if (categories.length > 0) {
    categoryList = (
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
        {categories.map((category) => (
          <button
            key={category.slug}
            type="button"
            onClick={() => handleCategorySelect(category.slug)}
            aria-pressed={selectedCategory === category.slug}
            className={categoryChipClassName(selectedCategory === category.slug)}
          >
            {categoryOptionLabel(category)}
          </button>
        ))}
      </div>
    );
  }

  let selectedLabel = selectedCategory;
  if (selectedOption) {
    selectedLabel = categoryOptionLabel(selectedOption);
  }

  let copyLabel = t('subscribe.copyUrl');
  if (copied) {
    copyLabel = t('subscribe.copied');
  }

  const features = [
    {
      title: t('subscribe.features.syncTitle'),
      desc: t('subscribe.features.syncDescription'),
    },
    {
      title: t('subscribe.features.anniversaryTitle'),
      desc: t('subscribe.features.anniversaryDescription'),
    },
    {
      title: t('subscribe.features.filterTitle'),
      desc: t('subscribe.features.filterDescription'),
    },
  ];

  return (
    <section id="subscribe" aria-labelledby="subscribe-heading">
      <div className="section-head">
        <h2 id="subscribe-heading" className="m-0">{t('subscribe.title')}</h2>
      </div>
      <p className="m-0 mt-4 text-sm leading-relaxed text-ink-2">
        {t('subscribe.description')}
      </p>

      {/* 分类筛选 */}
      <div className="mt-5 border-t border-rule pt-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="kicker">
            {t('subscribe.category')} {t('subscribe.single')}
          </span>
          {selectedCategory && (
            <button type="button" className="text-link" onClick={clearCategory}>
              {t('subscribe.clear')}
            </button>
          )}
        </div>

        {categoryList}

        {selectedCategory && (
          <p className="m-0 mt-3 text-xs leading-relaxed text-ink-2">
            {t('subscribe.selected')} <span className="text-ink">{selectedLabel}</span>
            <br />
            {t('subscribe.selectedHint')}
          </p>
        )}
      </div>

      {/* 订阅地址 */}
      <div className="mt-5 border-t border-rule pt-3">
        <label htmlFor="subscribe-url" className="kicker block">{t('subscribe.url')}</label>
        <input
          id="subscribe-url"
          value={currentUrl}
          readOnly
          onFocus={(event) => event.currentTarget.select()}
          className="mt-2 block w-full min-w-0 rounded-none border-0 border-b border-ink bg-transparent px-0 py-1.5 font-mono text-[13px] text-ink focus-visible:border-b-2 focus-visible:outline-none"
        />
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
          <button type="button" className="text-link" onClick={handleCopy} aria-live="polite">
            {copyLabel}
          </button>
          <button type="button" className="text-link text-accent-text" onClick={handleSubscribe}>
            {t('subscribe.subscribeNow')} →
          </button>
        </div>
      </div>

      <dl className="m-0 mt-5 border-t border-rule">
        {features.map((feature) => (
          <div key={feature.title} className="grid gap-y-0.5 border-b border-rule py-2.5 last:border-b-0 sm:grid-cols-[7rem_minmax(0,1fr)] sm:gap-x-4">
            <dt className="kicker pt-0.5">{feature.title}</dt>
            <dd className="m-0 text-sm text-ink-2">{feature.desc}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
